import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CoachSmartCatalogProspectSignals, SmartCatalogOptions, SmartCatalogOverridePayload } from "@/services/walter-assistant";
import { RotateCcw } from "lucide-react";
import { useMemo } from "react";
import { scopeFundingOptions } from "./smart-catalog.utils";

/** Clé publique d'un axe d'override, alignée sur le body de l'endpoint. */
type AxisKey = keyof SmartCatalogOverridePayload;

type Props = {
    /**
     * Snapshot enrichi des signaux (`detected_*` / `effective_*` /
     * `*_overridden`). Source de vérité du pré-remplissage et des badges.
     * `null` quand aucun payload n'a encore été reçu (pré-sélection manuelle) :
     * les dropdowns s'affichent vides, sans badge Détecté/Modifié.
     */
    signals: CoachSmartCatalogProspectSignals | null;
    /** Picklists des trois axes + mapping statut→financeurs. */
    options: SmartCatalogOptions;
    /** True quand une régénération est en vol — libellé sr-only uniquement. */
    isPending: boolean;
    /** Pose ou réinitialise un override (chaîne vide ⇒ reset). */
    onOverride: (payload: SmartCatalogOverridePayload) => void;
};

type AxisProps = {
    id: string;
    label: string;
    placeholder: string;
    /** Valeur effective (override ?? détecté). Vide ⇒ placeholder. */
    value: string;
    overridden: boolean;
    options: { code: string; name: string }[];
    onChange: (code: string) => void;
    onReset: () => void;
};

/**
 * Un axe = label + badge d'état (Détecté / Modifié) + sélecteur + bouton
 * reset (visible uniquement quand un override est actif). Le sélecteur
 * Radix exige une valeur non vide : on passe `undefined` quand l'axe
 * n'a pas de valeur effective, ce qui affiche le placeholder.
 */
function FilterAxis({ id, label, placeholder, value, overridden, options, onChange, onReset }: AxisProps) {
    const statusId = `${id}-status`;
    return (
        <div className="flex min-w-0 flex-1 basis-48 flex-col gap-1">
            <div className="flex items-center justify-start gap-2">
                <label htmlFor={id} className="text-sm font-medium text-gray-600">
                    {label}
                </label>
                {value ? (
                    <Badge id={statusId} variant={overridden ? "amber" : "blue"} className="shrink-0 px-1.5 py-0 text-[10px] font-medium uppercase tracking-wide">
                        {overridden ? "Modifié" : "Détecté"}
                    </Badge>
                ) : null}
            </div>
            <div className="flex items-center gap-1">
                <Select value={value || undefined} onValueChange={onChange}>
                    <SelectTrigger id={id} aria-describedby={value ? statusId : undefined} className="h-9 text-sm">
                        <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map(option => (
                            <SelectItem key={option.code} value={option.code}>
                                {option.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {overridden ? (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 shrink-0 text-gray-500"
                        onClick={onReset}
                        aria-label={`Réinitialiser « ${label} » sur la valeur détectée`}
                    >
                        <RotateCcw className="size-3.5" aria-hidden />
                    </Button>
                ) : null}
            </div>
        </div>
    );
}

/**
 * Bloc de contrôles d'override du Smart Catalog : trois sélecteurs
 * (profession, statut professionnel, financeur) pré-remplis avec la
 * valeur effective, éditables, avec reset par axe vers la détection.
 *
 * Les contrôles restent actifs pendant une régénération en vol — l'agent
 * peut enchaîner les overrides sans cooldown UI.
 */
export function SmartCatalogFilters({ signals, options, isPending, onOverride }: Props) {
    const effectiveProfession = (signals?.effective_profession_code ?? signals?.prospect_profession_code ?? "").trim();
    const effectiveStatus = (signals?.effective_professional_status ?? signals?.prospect_professional_status ?? "").trim();
    const effectiveFunding = (signals?.effective_funding_code ?? "").trim();

    const fundingOptions = useMemo(() => scopeFundingOptions(options, effectiveStatus, effectiveFunding), [options, effectiveStatus, effectiveFunding]);

    const setAxis = (axis: AxisKey, code: string) => onOverride({ [axis]: code });

    return (
        <section aria-label="Filtres du catalogue" className=" flex flex-col gap-3 rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
            <p className="sr-only" role="status" aria-live="polite">
                {isPending ? "Mise à jour du catalogue en cours." : "Catalogue à jour."}
            </p>
            <div className="flex flex-wrap items-end gap-3">
                <FilterAxis
                    id="smart-catalog-filter-profession"
                    label="Profession"
                    placeholder="Sélectionner…"
                    value={effectiveProfession}
                    overridden={Boolean(signals?.profession_code_overridden)}
                    options={options.professions}
                    onChange={code => setAxis("profession_code", code)}
                    onReset={() => setAxis("profession_code", "")}
                />
                <FilterAxis
                    id="smart-catalog-filter-status"
                    label="Statut professionnel"
                    placeholder="Sélectionner…"
                    value={effectiveStatus}
                    overridden={Boolean(signals?.professional_status_overridden)}
                    options={options.professional_statuses}
                    onChange={code => setAxis("professional_status", code)}
                    onReset={() => setAxis("professional_status", "")}
                />
                <FilterAxis
                    id="smart-catalog-filter-funding"
                    label="Financeur"
                    placeholder="Sélectionner…"
                    value={effectiveFunding}
                    overridden={Boolean(signals?.funding_code_overridden)}
                    options={fundingOptions}
                    onChange={code => setAxis("funding_code", code)}
                    onReset={() => setAxis("funding_code", "")}
                />
            </div>
        </section>
    );
}
